/**
 * Imaging machine register/edit form validations.
 */

import * as Yup from 'yup';
import type { Machine, MachineFormValues } from '../types/machine.types';

const AE_TITLE_REGEX = /^[A-Z0-9_-]+$/;
const AE_TITLE_MSG =
  'AE Title must be uppercase letters, numbers, underscore, or hyphen (max 16 characters).';

const IPV4_REGEX =
  /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const IPV4_MSG = 'Enter a valid IP address.';

const PORT_RANGE_MSG = 'Port must be between 1 and 65535.';

export const machineFormValidationSchema = Yup.object({
  machineName: Yup.string()
    .trim()
    .required('Machine name is required')
    .max(100, 'Maximum 100 characters.'),
  modalityType: Yup.string().trim().required('Modality type is required'),
  location: Yup.string()
    .trim()
    .required('Location is required')
    .max(200, 'Maximum 200 characters.'),
  aeTitle: Yup.string()
    .trim()
    .required('AE Title is required')
    .max(16, AE_TITLE_MSG)
    .test('ae-uppercase', AE_TITLE_MSG, value => {
      if (!value) {
        return false;
      }
      return AE_TITLE_REGEX.test(value.toUpperCase());
    }),
  ipAddress: Yup.string().trim().required('IP address is required').matches(IPV4_REGEX, IPV4_MSG),
  port: Yup.string()
    .trim()
    .required('Port is required')
    .matches(/^\d+$/, 'Port must be numeric')
    .test('port-range', PORT_RANGE_MSG, value => {
      if (!value) {
        return false;
      }
      const n = parseInt(value, 10);
      return n >= 1 && n <= 65535;
    }),
  active: Yup.boolean(),
});

export function getMachineFormFieldErrors(
  values: MachineFormValues,
  existingMachines: Machine[],
  editingMachineId?: number
): Partial<Record<keyof MachineFormValues, string>> {
  const errors: Partial<Record<keyof MachineFormValues, string>> = {};

  try {
    machineFormValidationSchema.validateSync(values, { abortEarly: false });
  } catch (err) {
    if (err instanceof Yup.ValidationError) {
      err.inner.forEach(innerErr => {
        const path = innerErr.path as keyof MachineFormValues | undefined;
        if (path && !errors[path]) {
          errors[path] = innerErr.message;
        }
      });
    }
  }

  const normalizedAe = values.aeTitle.trim().toUpperCase();
  if (normalizedAe && !errors.aeTitle) {
    const duplicateAe = existingMachines.some(
      m => m.id !== editingMachineId && m.aeTitle.toUpperCase() === normalizedAe
    );
    if (duplicateAe) {
      errors.aeTitle = 'This AE Title is already registered in your facility.';
    }
  }

  const ip = values.ipAddress.trim();
  const port = parseInt(values.port, 10);
  if (ip && Number.isFinite(port) && !errors.ipAddress && !errors.port) {
    const duplicateEndpoint = existingMachines.some(
      m =>
        m.id !== editingMachineId &&
        m.active &&
        values.active &&
        m.ipAddress.trim() === ip &&
        m.port === port
    );
    if (duplicateEndpoint) {
      errors.ipAddress = 'Another active machine already uses this IP address and port.';
    }
  }

  return errors;
}

export function hasMachineFormErrors(
  errors: Partial<Record<keyof MachineFormValues, string>>
): boolean {
  return Object.keys(errors).length > 0;
}
